import React, {useMemo} from "react";
import * as THREE from "three";
import {ExtrudeGeometry, ShapeGeometry} from "three";
import {GetEntity} from "../../../../../Server/DB/entities.js";
import {ResolveObjPosInWorld} from "../../../../../Server/DB/mapObjects/$mapObject.js";
import {MapObject} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {DMap} from "../../../../../Server/DB/maps/@Map.js";
import {MapState} from "../../../../../Store/main/maps/@MapState.js";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import {Vec2, Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {store} from "../../../../../Store/index.js";
import {mapZIndexes} from "../MapZIndexes.js";
import {useBase64ImageStrAsTexture} from "../../../../../Utils/ThreeJS/3JSHooks.js";
import {ColorArrayToTHREEColor} from "../../../../../Utils/ThreeJS/ColorUtils.js";
import {ObjMesh} from "../../../../../Server/DB/mapObjects/@MapObject/ObjMesh.js";

export const MeshUI = observer_mgl_r3((props: {mesh: ObjMesh, obj: MapObject, map: DMap, mapState: MapState, textureOverride?: string|n, uvsOverride_asPointBounds?: boolean})=>{
	const {mesh, obj, map, mapState, textureOverride, uvsOverride_asPointBounds} = props;
	const meshIndex = obj.meshes.indexOf(mesh);
	const toolState = store.main.maps.tools.toolStates.meshModify;
	const objPos = ResolveObjPosInWorld(obj);

	const materialColor = useMemo(()=>ColorArrayToTHREEColor(mesh.color), [mesh.color]);

	const textureEntity = GetEntity(mesh.texture_id);
	const textureStr = textureOverride ?? textureEntity?.image_embedded;
	//const texture = useMemo(()=>DataUri(textureEntity?.image_embedded), [textureEntity?.image_embedded]);
	const texture = useBase64ImageStrAsTexture(textureStr);

	const pendingMeshUpdateHere = toolState.pendingObjMeshUpdates.get(obj.id)?.meshIndex == meshIndex ? toolState.pendingObjMeshUpdates.get(obj.id) : null;
	const meshPoints = useMemo(()=>{
		return pendingMeshUpdateHere?.newPoints.map(a=>Vec2.FromSimple(a)) ?? mesh.points.map(a=>Vec2.FromSimple(a));
	}, [mesh.points, pendingMeshUpdateHere]);
	//const meshPoints_3j = meshPoints.map(a=>a.ToVec3().To3JPos());
	//const meshPoints_3j_flat = meshPoints_3j.map(a=>[a.x, a.y, a.z]).flat();

	const meshPoints_asGeometry = useMemo(()=>{
		const geometry = CreateComplexPolygonMesh(meshPoints, [], 0);
		if (uvsOverride_asPointBounds) {
			CalculateUVsAsRectEncompassingPoints(geometry);
		} else {
			CalculateUVsAsPointPositions(geometry, mesh.texture_size ?? new Vec2(1, 1), objPos.ToVec2(), mesh.texture_rotation ?? 0);
		}
		geometry.attributes.position.needsUpdate = true;
		geometry.attributes.uv.needsUpdate = true;
		return geometry;
	}, [meshPoints, uvsOverride_asPointBounds, mesh.texture_size, mesh.texture_rotation, objPos]);

	const opacity = mesh.opacity ?? 1;
	return (
		<mesh position={Vec3.zero.NewZ(mesh.zOffset ?? 0).To3JPos()}>
			{/*<bufferGeometry attach="geometry">
				<bufferAttribute attach="attributes-position" args={[new Float32Array(meshPoints_3j_flat), 3]} />
				<bufferAttribute attach="attributes-normal" args={[new Float32Array(meshPoints_3j_flat.map(a=>[0, 1, 0]).flat()), 3]} />
				<bufferAttribute attach="attributes-uv" args={[new Float32Array(meshPoints.map(a=>[a.x, a.y]).flat()), 2]} />
				<bufferAttribute attach="attributes-index" args={[new Uint16Array(meshPoints_3j_flat.map(a=>1)), 1]} /> {/#* all vertices are for the same face, so just use 1 index *#/}
			</bufferGeometry>*/}
			<primitive attach="geometry" object={meshPoints_asGeometry}/>
			<meshBasicMaterial attach="material" color={materialColor} map={texture} opacity={opacity}
				transparent={true}/>
		</mesh>
	);
});

function CreateComplexPolygonMesh(points: Vec2[], holes = [], height = 0): ShapeGeometry | ExtrudeGeometry {
	// Create the main shape
	const shape = new THREE.Shape();
	shape.moveTo(points[0].x, points[0].y);
	for (let i = 1; i < points.length; i++) {
		shape.lineTo(points[i].x, points[i].y);
	}

	// Add holes if any
	/*if (holes.length > 0) {
		for (const holePoints of holes) {
			const holePath = new THREE.Path();
			holePath.moveTo(holePoints[0].x, holePoints[0].y);
			for (let i = 1; i < holePoints.length; i++) {
				holePath.lineTo(holePoints[i].x, holePoints[i].y);
			}
			shape.holes.push(holePath);
		}
	}*/

	// create geometry - use ExtrudeGeometry for height if needed
	let geometry;
	if (height > 0) {
		geometry = new ExtrudeGeometry(shape, {
			depth: height,
			bevelEnabled: false
		});
	} else {
		geometry = new ShapeGeometry(shape);
	}
	return geometry;
}

export function CalculateUVsAsPointPositions(geometry: ShapeGeometry | ExtrudeGeometry, scale: Vec2, basePos = Vec2.zero, rotation = 0) {
	const positions = geometry.attributes.position;
	const uvs = geometry.attributes.uv;

	// Convert rotation from degrees to radians
	const rotationRad = (rotation * Math.PI) / 180;
	const cos = Math.cos(rotationRad);
	const sin = Math.sin(rotationRad);

	for (let i = 0; i < positions.count; i++) {
		const x = positions.getX(i) + basePos.x;
		const y = positions.getY(i) + basePos.y;

		// Apply rotation to UV coordinates
		const rotatedX = x * cos - y * sin;
		const rotatedY = x * sin + y * cos;

		uvs.setXY(i, rotatedX / scale.x, rotatedY / scale.y);
	}
	uvs.needsUpdate = true;
}
export function CalculateUVsAsRectEncompassingPoints(geometry: ShapeGeometry | ExtrudeGeometry) {
	const positions = geometry.attributes.position;
	const uvs = geometry.attributes.uv;

	// find the bounding box of the points
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	for (let i = 0; i < positions.count; i++) {
		const x = positions.getX(i);
		const y = positions.getY(i);
		minX = Math.min(minX, x);
		minY = Math.min(minY, y);
		maxX = Math.max(maxX, x);
		maxY = Math.max(maxY, y);
	}

	const width = maxX - minX;
	const height = maxY - minY;
	for (let i = 0; i < positions.count; i++) {
		const x = positions.getX(i) - minX;
		const y = positions.getY(i) - minY;
		uvs.setXY(i, x / width, y / height);
	}
	uvs.needsUpdate = true;
}